import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  AccessTime,
  CheckCircle,
  Pending,
  Person,
  LocalHospital,
  Bed,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getICURoundsDashboard } from "../../../../api/icu";

const RoundsDashboard = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();

  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["icu-rounds-dashboard"],
    queryFn: () => getICURoundsDashboard(token),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t(
            "icu.rounds.dashboard.error_loading",
            "Error loading rounds dashboard"
          )}
          {error && `: ${error.message}`}
        </Alert>
      </Box>
    );
  }

  const morningRounds = dashboardData?.morning_rounds || [];
  const eveningRounds = dashboardData?.evening_rounds || [];
  const nightRounds = dashboardData?.night_rounds || [];
  const scheduledRounds = dashboardData?.scheduled_rounds || [];
  const pendingCount = dashboardData?.pending_count || 0;
  const completedCount = dashboardData?.completed_count || 0;

  const getStatusChip = (round) => {
    const status = round.status;
    switch (status) {
      case 1: // Pending
        return <Chip label={t("icu.rounds.pending", "Pending")} color="warning" size="small" />;
      case 2: // In Progress
        return <Chip label={t("icu.rounds.in_progress", "In Progress")} color="info" size="small" />;
      case 3: // Completed
        return <Chip label={t("icu.rounds.completed", "Completed")} color="success" size="small" />;
      default:
        return null;
    }
  };

  const getRoundTypeText = (type) => {
    switch (type) {
      case 1:
        return t("icu.rounds.morning", "Morning Round");
      case 2:
        return t("icu.rounds.evening", "Evening Round");
      case 3:
        return t("icu.rounds.night", "Night Round");
      default:
        return t("icu.rounds.round", "Round");
    }
  };

  const renderRoundCard = (round) => {
    return (
      <Card
        key={round.id}
        variant="outlined"
        sx={{ mb: 2, cursor: "pointer" }}
        onClick={() => navigate(`/icu/rounds/${round.id}`)}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {round.patient_name || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Bed fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                {round.admission_id ? `Admission #${round.admission_id}` : "-"}
              </Typography>
            </Box>
            {getStatusChip(round)}
          </Box>
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <Person fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {round.doctor_name || "-"}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <AccessTime fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {round.round_date
                ? new Date(round.round_date).toLocaleTimeString()
                : "-"}
            </Typography>
          </Box>
          {round.nurses_present_names && round.nurses_present_names.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t("icu.rounds.nurses_present", "Nurses")}:{" "}
                {round.nurses_present_names.join(", ")}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderScheduledRound = (scheduled) => {
    return (
      <ListItem
        key={`scheduled-${scheduled.admission_id}-${scheduled.round_type}`}
        sx={{
          border: "1px dashed",
          borderColor: "warning.main",
          borderRadius: 1,
          mb: 1,
          bgcolor: "warning.light",
        }}
      >
        <ListItemIcon>
          <Pending color="warning" />
        </ListItemIcon>
        <ListItemText
          primary={scheduled.patient_name || "-"}
          secondary={
            <>
              {getRoundTypeText(scheduled.round_type)} • {scheduled.bed_info}
              <br />
              <Typography variant="caption">
                {t("icu.rounds.scheduled_time", "Scheduled")}:{" "}
                {scheduled.scheduled_time || "08:00"}
              </Typography>
            </>
          }
        />
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/icu/rounds/new?admission=${scheduled.admission_id}&type=${scheduled.round_type}`);
          }}
        >
          {t("icu.rounds.start_round", "Start Round")}
        </Button>
      </ListItem>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t("icu.rounds.dashboard.title", "ICU Rounds Dashboard")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/icu/rounds/new")}
        >
          {t("icu.rounds.new_round", "New Round")}
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Pending color="warning" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold" color="warning.main">
              {pendingCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("icu.rounds.pending", "Pending")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {completedCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("icu.rounds.completed", "Completed")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <LocalHospital color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              {scheduledRounds.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("icu.rounds.scheduled", "Scheduled")}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <AccessTime color="info" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h4" fontWeight="bold" color="info.main">
              {morningRounds.length + eveningRounds.length + nightRounds.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("icu.rounds.total_today", "Today's Rounds")}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Scheduled Rounds (Need to be started) */}
        {scheduledRounds.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="warning.main">
                {t("icu.rounds.scheduled_rounds", "Scheduled Rounds")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(
                  "icu.rounds.scheduled_description",
                  "Rounds that need to be started"
                )}
              </Typography>
              <List>
                {scheduledRounds.map(renderScheduledRound)}
              </List>
            </Paper>
          </Grid>
        )}

        {/* Morning Rounds */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("icu.rounds.morning_rounds", "Morning Rounds")}
            </Typography>
            {morningRounds.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("icu.rounds.no_morning_rounds", "No morning rounds today")}
              </Typography>
            ) : (
              morningRounds.map(renderRoundCard)
            )}
          </Paper>
        </Grid>

        {/* Evening Rounds */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("icu.rounds.evening_rounds", "Evening Rounds")}
            </Typography>
            {eveningRounds.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("icu.rounds.no_evening_rounds", "No evening rounds today")}
              </Typography>
            ) : (
              eveningRounds.map(renderRoundCard)
            )}
          </Paper>
        </Grid>

        {/* Night Rounds */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("icu.rounds.night_rounds", "Night Rounds")}
            </Typography>
            {nightRounds.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("icu.rounds.no_night_rounds", "No night rounds today")}
              </Typography>
            ) : (
              nightRounds.map(renderRoundCard)
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RoundsDashboard;

